import { Component } from '@theme/component';

/**
 * `<bundle-bottom-bar>` — viewport-pinned summary with collapsed + expanded
 * states (Figma 384:2591 / 384:5491).
 *
 * Collapsed (default): title + price + "View Bundle". Expanded: one slot per
 * step ("Choose Your X" → filled product card on save) + title/price + close.
 * Price is committed/lowest-config from `bundle:update`. Pure presentation —
 * mutates state only through the builder's declarative bindings.
 */
export class BundleBottomBarComponent extends Component {
  #builder = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#builder = /** @type {any} */ (this.closest('bundle-builder'));
    this.#builder?.addEventListener('bundle:update', this.#onUpdate);
    this.refs.viewBtn?.addEventListener('click', this.#expand);
    this.refs.close?.addEventListener('click', this.#collapse);

    this.#resizeObserver = new ResizeObserver(this.#reserveSpace);
    this.#resizeObserver.observe(this);
    this.#reserveSpace();
    this.#builder?.requestUpdate?.();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#builder?.removeEventListener('bundle:update', this.#onUpdate);
    this.refs.viewBtn?.removeEventListener('click', this.#expand);
    this.refs.close?.removeEventListener('click', this.#collapse);
    this.#resizeObserver?.disconnect();
    this.#builder?.style.removeProperty('--bb-bar-height');
  }

  #expand = () => {
    this.removeAttribute('data-collapsed');
    this.#reserveSpace();
  };

  #collapse = () => {
    this.setAttribute('data-collapsed', '');
    this.#reserveSpace();
  };

  /** Reserve the bar's real height on the builder so it never covers content. */
  #reserveSpace = () => {
    this.#builder?.style.setProperty('--bb-bar-height', `${this.offsetHeight}px`);
  };

  /** @param {CustomEvent} event */
  #onUpdate = (event) => {
    const builder = this.#builder;
    if (!builder) return;
    const { totals } = event.detail;

    // ── Price (synced across both collapsed + expanded summaries) ──
    const hasSavings = totals.savings > 0;
    for (const el of this.querySelectorAll('.bb-bar__total')) el.textContent = builder.money(totals.total);
    for (const el of this.querySelectorAll('.bb-bar__compare')) {
      el.textContent = hasSavings ? builder.money(totals.subtotal) : '';
      el.toggleAttribute('hidden', !hasSavings);
    }
    for (const el of this.querySelectorAll('.bb-bar__savings')) {
      el.textContent = totals.percent > 0 ? `SAVE ${totals.percent}%` : '';
      el.toggleAttribute('hidden', totals.percent <= 0);
    }

    // ── Slots (committed selection → filled card) ──
    const slots = this.#asArray(this.refs.slots);
    const thumbs = this.#asArray(this.refs.slotThumb);
    const filled = this.#asArray(this.refs.slotFilled);
    const values = this.#asArray(this.refs.slotValue);

    slots.forEach((slot, i) => {
      const stepIndex = Number(slot.dataset.step);
      const sel = builder.getStepSelection(stepIndex);
      const product = sel?.product ?? null;
      const isFilled = Boolean(product);

      slot.toggleAttribute('data-filled', isFilled);
      slot.querySelector('.bb-bar__slot-empty')?.toggleAttribute('hidden', isFilled);
      filled[i]?.toggleAttribute('hidden', !isFilled);

      if (values[i]) {
        // Name the chosen VARIANT too when the product offers a real choice
        // (e.g. the kids bags' size) — otherwise the summary can't distinguish
        // the two differently-priced lines the bar is already pricing.
        values[i].textContent = product ? `${sel.step.label}: ${product.color || product.title}${this.#variantSuffix(sel)}` : '';
      }

      const thumb = thumbs[i];
      if (thumb) {
        const img = thumb.querySelector('img');
        const src = product?.image || product?.swatch || '';
        if (img && src) {
          img.src = src;
          img.alt = product?.title ?? '';
        }
        thumb.toggleAttribute('hidden', !isFilled || !src);
      }
    });

    // Bundle complete = every step has a committed product. Drives the mobile
    // Checkout CTA, which is only shown (via CSS) when `[data-complete]`.
    const steps = builder.config?.steps ?? [];
    const allChosen = steps.length > 0 && steps.every((_, i) => Boolean(builder.getStepSelection(i)?.product));
    this.toggleAttribute('data-complete', allChosen);
    // The CTA changes the bar's height; re-reserve so content isn't covered.
    this.#reserveSpace();
  };

  /**
   * " — 9-12 years old 31” (H) 8” (W)" for a multi-variant product, empty for a
   * single-variant one (where there is nothing to disambiguate).
   * @param {{product:any, variant:any}} sel
   */
  #variantSuffix(sel) {
    if ((sel?.product?.variants?.length ?? 0) < 2 || !sel?.variant) return '';
    const options = Array.isArray(sel.variant.options) ? sel.variant.options.filter(Boolean) : [];
    const label = options.length ? options.join(' / ') : sel.variant.title;
    return label ? ` — ${label}` : '';
  }

  /** @param {Element | Element[] | undefined} ref @returns {Element[]} */
  #asArray(ref) {
    if (!ref) return [];
    return Array.isArray(ref) ? ref : [ref];
  }
}

if (!customElements.get('bundle-bottom-bar')) {
  customElements.define('bundle-bottom-bar', BundleBottomBarComponent);
}
